import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, CheckCheck, LogOut, Menu, MoonStar, Sun } from 'lucide-react';
import { useAuthStore } from '@/store';
import { useUIStore } from '@/store';
import { cn } from '@/lib/utils';
import { inboxApi, type InboxMessage } from '@/api/inbox';
import { resolvePublicApiBaseUrl } from '@/lib/api-base-url';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const INBOX_SOUND_KEY = 'inbox-sound-enabled';

function playNotificationBeep() {
  if (typeof window === 'undefined') return;
  const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return;
  const ctx = new Ctx();
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = 880;
  gain.gain.value = 0.04;
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start();
  oscillator.stop(ctx.currentTime + 0.12);
}

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout, token } = useAuthStore();
  const { inboxUnreadCount, setInboxUnreadCount, theme, setTheme } = useUIStore();
  const isLight = theme === 'light';
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(INBOX_SOUND_KEY);
    return raw !== '0';
  });
  const panelRef = useRef<HTMLDivElement | null>(null);
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(INBOX_SOUND_KEY, soundEnabled ? '1' : '0');
  }, [soundEnabled]);

  const unreadCount = useMemo(
    () => messages.filter((m) => !m.read).length,
    [messages],
  );
  const inboxScope = user?.scope === 'platform' ? 'platform' : 'tenant';
  const inboxBasePath = inboxScope === 'platform' ? '/api/platform/inbox' : '/api/inbox';

  useEffect(() => {
    setInboxUnreadCount(unreadCount);
  }, [unreadCount, setInboxUnreadCount]);

  useEffect(() => {
    if (!token) return;
    // 与 api/client.ts 的 baseURL 规则保持一致
    const base = resolvePublicApiBaseUrl().replace(/\/$/, '');
    const streamPath = `${inboxBasePath}/stream`;
    const streamUrl = base
      ? base.startsWith('http')
        ? `${base}${streamPath}?token=${encodeURIComponent(token)}`
        : `${window.location.origin}${base}${streamPath}?token=${encodeURIComponent(token)}`
      : `${window.location.origin}${streamPath}?token=${encodeURIComponent(token)}`;
    const source = new EventSource(streamUrl, { withCredentials: true });

    source.addEventListener('inbox', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data) as {
          messages?: InboxMessage[];
          unreadCount?: number;
        };
        const list = Array.isArray(payload.messages) ? payload.messages : [];
        setMessages(list);
        setInboxUnreadCount(payload.unreadCount ?? list.filter((m) => !m.read).length);

        const unreadNow = list.filter((m) => !m.read);
        const unseen = unreadNow.filter((m) => !notifiedIdsRef.current.has(m.id));
        if (unseen.length > 0) {
          unseen.forEach((m) => notifiedIdsRef.current.add(m.id));
          const latest = unseen[0];
          toast.info(`新站内信：${latest.title}`, {
            description: latest.content,
          });
          if (soundEnabled) {
            playNotificationBeep();
          }
        }

        const latestUnreadSet = new Set(unreadNow.map((m) => m.id));
        notifiedIdsRef.current.forEach((id) => {
          if (!latestUnreadSet.has(id)) {
            notifiedIdsRef.current.delete(id);
          }
        });
      } catch {}
    });

    source.addEventListener('error', () => {
      // EventSource 会自动重连，这里无需额外处理
    });

    return () => {
      source.close();
    };
  }, [token, inboxBasePath, setInboxUnreadCount, soundEnabled]);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target as Node)) {
        setInboxOpen(false);
      }
    }
    if (inboxOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [inboxOpen]);

  async function loadInbox() {
    try {
      setInboxLoading(true);
      const res = await inboxApi.getMyMessages(inboxScope);
      const payload = res.data as unknown;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray((payload as { data?: InboxMessage[] })?.data)
          ? ((payload as { data?: InboxMessage[] }).data ?? [])
          : [];
      setMessages(list);
      setInboxUnreadCount(list.filter((m) => !m.read).length);
    } catch {
      setMessages([]);
      setInboxUnreadCount(0);
    } finally {
      setInboxLoading(false);
    }
  }

  async function handleToggleInbox() {
    const next = !inboxOpen;
    setInboxOpen(next);
    if (next && messages.length === 0) {
      await loadInbox();
    }
  }

  async function handleMarkAsRead(message: InboxMessage) {
    if (message.read) {
      if (message.actionPath) {
        navigate(message.actionPath);
        setInboxOpen(false);
      }
      return;
    }
    try {
      await inboxApi.markAsRead(message.id, inboxScope);
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, read: true } : m)),
      );
      if (message.actionPath) {
        navigate(message.actionPath);
        setInboxOpen(false);
      }
    } catch {}
  }

  async function handleMarkAllAsRead() {
    try {
      await inboxApi.markAllAsRead(inboxScope);
      setMessages((prev) => prev.map((m) => ({ ...m, read: true })));
      setInboxUnreadCount(0);
    } catch {}
  }

  return (
    <header
      className={cn(
        'relative z-20 flex h-16 shrink-0 items-center justify-between border-b px-4 backdrop-blur-xl sm:px-6',
        isLight
          ? 'border-slate-300/80 bg-white/85 shadow-[0_1px_0_rgba(15,23,42,0.08)]'
          : 'border-cyan-500/15 bg-zinc-950/75 shadow-[0_1px_0_rgba(34,211,238,0.08)]',
      )}
    >
      <div
        className={cn(
          'absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
          isLight ? 'via-cyan-500/30' : 'via-cyan-400/35',
        )}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-zinc-300 transition-colors hover:border-cyan-400/30 hover:bg-cyan-500/10 hover:text-cyan-200 lg:hidden"
          aria-label="打开菜单"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={() => setTheme(isLight ? 'dark' : 'light')}
          className={cn(
            'rounded-xl border p-2.5 transition-all',
            isLight
              ? 'border-slate-300 bg-slate-100 text-slate-600 hover:border-cyan-400/45 hover:text-cyan-700'
              : 'border-white/10 bg-white/[0.04] text-zinc-400 hover:border-cyan-400/25 hover:text-cyan-200',
          )}
          aria-label={isLight ? '切换深色主题' : '切换浅色主题'}
          title={isLight ? '深色模式' : '浅色模式'}
        >
          {isLight ? <MoonStar className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </button>
        <div className="relative" ref={panelRef}>
          <button
            type="button"
            onClick={() => void handleToggleInbox()}
            className={cn(
              'relative rounded-xl border p-2.5 transition-all',
              isLight
                ? 'border-slate-300 bg-slate-100 text-slate-600 hover:border-cyan-400/45 hover:text-cyan-700'
                : 'border-white/10 bg-white/[0.04] text-zinc-400 hover:border-cyan-400/25 hover:text-cyan-200',
            )}
            aria-label="站内信"
          >
            <Bell className="h-5 w-5" />
            {inboxUnreadCount > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-fuchsia-500 px-1 text-[10px] font-bold text-white shadow-[0_0_12px_rgba(217,70,239,0.6)]">
                {inboxUnreadCount > 9 ? '9+' : inboxUnreadCount}
              </span>
            )}
          </button>
          {inboxOpen && (
            <div
              className={cn(
                'absolute right-0 top-[calc(100%+10px)] z-50 w-[340px] rounded-xl border shadow-xl',
                isLight
                  ? 'border-slate-300 bg-white'
                  : 'border-white/10 bg-zinc-950/95',
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-between border-b px-3 py-2',
                  isLight ? 'border-slate-200' : 'border-white/10',
                )}
              >
                <p className={cn('text-sm font-semibold', isLight ? 'text-slate-800' : 'text-zinc-100')}>
                  站内信
                </p>
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors',
                    isLight
                      ? 'text-slate-600 hover:bg-cyan-50 hover:text-cyan-700'
                      : 'text-zinc-400 hover:bg-white/10 hover:text-cyan-200',
                  )}
                  onClick={() => void handleMarkAllAsRead()}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  全部已读
                </button>
              </div>
              <div
                className={cn(
                  'flex items-center justify-between border-b px-3 py-2 text-xs',
                  isLight ? 'border-slate-200 text-slate-600' : 'border-white/10 text-zinc-400',
                )}
              >
                <span>声音提醒</span>
                <button
                  type="button"
                  className={cn(
                    'rounded-md px-2 py-1 transition-colors',
                    isLight
                      ? 'hover:bg-cyan-50 hover:text-cyan-700'
                      : 'hover:bg-white/10 hover:text-cyan-200',
                  )}
                  onClick={() => setSoundEnabled((v) => !v)}
                >
                  {soundEnabled ? '已开启' : '已关闭'}
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto p-2">
                {inboxLoading ? (
                  <p className={cn('px-2 py-6 text-center text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                    加载中...
                  </p>
                ) : messages.length === 0 ? (
                  <p className={cn('px-2 py-6 text-center text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                    暂无站内信
                  </p>
                ) : (
                  messages.map((msg) => (
                    <button
                      key={msg.id}
                      type="button"
                      onClick={() => void handleMarkAsRead(msg)}
                      className={cn(
                        'mb-1 block w-full rounded-lg border p-3 text-left transition-colors last:mb-0',
                        isLight
                          ? msg.read
                            ? 'border-slate-200 hover:bg-slate-50'
                            : 'border-cyan-300/70 bg-cyan-50/80 hover:bg-cyan-50'
                          : msg.read
                            ? 'border-white/10 hover:bg-white/5'
                            : 'border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/15',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn('line-clamp-1 text-sm font-medium', isLight ? 'text-slate-900' : 'text-zinc-100')}>
                          {msg.title}
                        </p>
                        {!msg.read && (
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-fuchsia-500" />
                        )}
                      </div>
                      <p className={cn('mt-1 line-clamp-2 text-xs', isLight ? 'text-slate-600' : 'text-zinc-400')}>
                        {msg.content}
                      </p>
                      <p className={cn('mt-2 text-[11px]', isLight ? 'text-slate-400' : 'text-zinc-500')}>
                        {new Date(msg.createdAt).toLocaleString('zh-CN')}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className={cn(
            'flex items-center gap-2 border-l pl-3 sm:gap-3 sm:pl-4',
            isLight ? 'border-slate-300/90' : 'border-white/10',
          )}
        >
          <div className="flex items-center gap-2.5">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                'bg-gradient-to-br from-cyan-500 to-fuchsia-600 text-sm font-bold text-white',
                'shadow-[0_0_20px_-4px_rgba(34,211,238,0.55)]',
              )}
            >
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="hidden min-w-0 text-sm sm:block">
              <div className={cn('truncate font-medium', isLight ? 'text-slate-900' : 'text-zinc-100')}>
                {user?.name}
              </div>
              <div className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                {user?.role === 'admin' ? '管理员' : '用户'}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={logout}
            className="rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-zinc-400 transition-all hover:border-red-400/30 hover:bg-red-500/10 hover:text-red-300"
            title="退出登录"
            aria-label="退出登录"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
}
